import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit, Signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
    ShButtonComponent,
    ShSpinnerComponent,
    ShTabItemComponent,
    ShTabsComponent,
    DialogService as ShuiDialogService
} from '@gbr-monorepo/gbr-shui';
import { Season, SeasonStatus } from '@gbr-monorepo/interfaces';
import { TranslateModule } from '@ngx-translate/core';
import { select, Store } from '@ngxs/store';
import { filter, finalize, map, switchMap, take, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { BackHomeComponent } from '../../../shared/components/back-home/back-home.component';
import { UserApplicationItem } from '../../../shared/interfaces/ApplicationItem';
import { FinancialOverviewResponse } from '../../models/season-financial.model';
import { SeasonService } from '../../services/season.service';
import {
    AutoSelectForPayout,
    CloseSeason,
    LoadFinancialOverview,
    LoadSeason,
    ReleasePayout,
    ResetAutoSelectedApplications
} from '../../state/season/season.action';
import { SeasonState } from '../../state/season/season.state';
import { SeasonApplicationsTableComponent } from './applications-table/applications-table.component';
import { AutoSelectApplicationsDialogComponent } from './auto-select-applications-dialog/auto-select-applications-dialog.component';
import { BudgetCardComponent } from './budget-card/budget-card.component';
import { CloseSeasonDialogComponent } from './close-season-dialog/close-season-dialog.component';
import { FinancialCardComponent } from './financial-card/financial-card.component';
import { ReleasePayoutDialogComponent } from './release-payout-dialog/release-payout-dialog.component';
import { SalaryCardComponent } from './salary-card/salary-card.component';
import { SeasonDetailsExportApplicationButtonComponent } from './season-details-export-application-button/season-details-export-application-button.component';
import { SocialCriteriaCardComponent } from './social-criteria-card/social-criteria-card.component';
import { TimeframeCardComponent } from './timeframe-card/timeframe-card.component';
@Component({
    selector: 'app-season-details',
    templateUrl: './season-details.component.html',
    imports: [
        FinancialCardComponent,
        CommonModule,
        SeasonApplicationsTableComponent,
        TimeframeCardComponent,
        BudgetCardComponent,
        SocialCriteriaCardComponent,
        SalaryCardComponent,
        TranslateModule,
        ReactiveFormsModule,
        RouterModule,
        SeasonDetailsExportApplicationButtonComponent,
        BackHomeComponent,
        ShButtonComponent,
        ShSpinnerComponent,
        ShTabItemComponent,
        ShTabsComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [CurrencyPipe]
})
export class SeasonDetailsComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly route = inject(ActivatedRoute);
    private readonly dialog = inject(ShuiDialogService);
    private readonly router = inject(Router);
    private readonly seasonService = inject(SeasonService);
    private readonly destroyRef = inject(DestroyRef);

    protected season: Signal<Season> = select(SeasonState.getSeason);
    protected isLoading: Signal<boolean> = select(SeasonState.isLoadingSeasonDetails);
    protected financialOverviewSignal: Signal<FinancialOverviewResponse> = select(SeasonState.getFinancialOverview);
    protected applicationsSignal: Signal<UserApplicationItem[]> = select(SeasonState.getApplications);
    protected seasonHasPayoutApplicationsSignal: Signal<boolean> = select(SeasonState.hasPayoutApplications);

    protected isApplicationsSelectedForPayoutSignal: Signal<boolean> = computed(() => {
        const applications = this.applicationsSignal();
        return applications?.some(application => application.isSelectedForPayout);
    });

    protected isBudgetAvailableSignal: Signal<boolean> = computed(() => {
        const financialOverview = this.financialOverviewSignal();
        return financialOverview?.availableBudget > 0;
    });

    protected hasSeasonApplicationPeriodEndedSignal: Signal<boolean> = computed(() => {
        const season = this.season();
        const currentDate = new Date();
        return season.applicationDeadline ? new Date(season.applicationDeadline) <= currentDate : false;
    });

    protected isSeasonClosedSignal: Signal<boolean> = computed(() => {
        const season = this.season();
        return season.status === SeasonStatus.CLOSED;
    });

    protected isAutoSelectDisabledSignal: Signal<boolean> = computed(() => {
        const season = this.season();
        const financialOverview = this.financialOverviewSignal();
        return season.status === SeasonStatus.CLOSED || financialOverview?.totalApplications === 0;
    });

    protected readonly seasonStateBackgroundClass: Record<SeasonStatus, string> = {
        [SeasonStatus.ACTIVE]: 'bg-[rgb(var(--ui-6))]',
        [SeasonStatus.DRAFT]: 'bg-[rgb(var(--ui-2))]',
        [SeasonStatus.CLOSED]: 'bg-[rgb(var(--ui-5))]'
    };
    protected seasonStatus = SeasonStatus;
    protected isDownloading = false;
    protected activeTab = 'applications';
    private seasonId: string;

    protected readonly isExportApplicationsButtonEnabled = environment.featureFlags.exportApplicationsButtonEnabled;

    ngOnInit(): void {
        this.loadData();
    }

    protected loadData(): void {
        this.route.params
            .pipe(
                map(params => params['seasonId']),
                tap(seasonId => {
                    this.store.dispatch(new LoadSeason({ seasonId: seasonId }));
                    this.store.dispatch(new LoadFinancialOverview({ seasonId: seasonId }));
                    this.seasonId = seasonId;
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    protected autoSelectApplications(): void {
        const dialogRef = this.dialog.open(AutoSelectApplicationsDialogComponent, {
            title: 'seasons.season-overview.dialogs.autoselect-applications-dialog.title',
            width: '560px'
        });

        dialogRef.afterClosed
            .pipe(
                filter((result): result is { applicationCutOffDate: Date; percentage: number } => !!result),
                switchMap(({ applicationCutOffDate, percentage }) =>
                    this.store.dispatch(
                        new AutoSelectForPayout({
                            applicationCutOffDate,
                            percentage
                        })
                    )
                ),
                tap(() => this.loadData())
            )
            .subscribe();
    }

    protected resetAutoSelectedApplications(): void {
        this.store.dispatch(new ResetAutoSelectedApplications());
    }

    protected releasePayout(): void {
        const dialogRef = this.dialog.open(ReleasePayoutDialogComponent, {});

        dialogRef.afterClosed
            .pipe(
                filter((result): result is { payoutCutOffDate: Date } => !!result),
                switchMap(({ payoutCutOffDate }) =>
                    this.store.dispatch(
                        new ReleasePayout({
                            payoutCutOffDate
                        })
                    )
                ),
                tap(() => this.router.navigate(['payout-success']))
            )
            .subscribe();
    }

    protected closeSeason(): void {
        const dialogRef = this.dialog.open(CloseSeasonDialogComponent, {
            title: 'seasons.season-details.dialogs.close-season-dialog.title',
            data: {
                message: 'seasons.season-details.dialogs.close-season-dialog.description'
            }
        });

        dialogRef.afterClosed
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(confirmed => !!confirmed),
                switchMap(() => this.store.dispatch(new CloseSeason(this.seasonId)))
            )
            .subscribe();
    }

    protected downloadPayoutFiles(): void {
        this.isDownloading = true;
        this.seasonService
            .downloadPayoutFiles(this.season().id, `Payout_Files_${this.season().title}.xlsx`)
            .pipe(
                take(1),
                finalize(() => (this.isDownloading = false))
            )
            .subscribe();
    }
}
